// DoorPanel.jsx
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Box, Grid, Typography } from "@mui/material";
import { useStepperContext } from "../StepperContext";
import { endpoints } from "../../../utils/endpoints";
import {
  ACTION_TYPE,
  DIGITAL_TWIN_ACTION,
  SKIP_BATTERY_DETECTION_REASON,
} from "../common";
import DoorActionButtons from "./DoorActionButtons";
import DoorCard from "./DoorCard";

const CONFIG = {
  [DIGITAL_TWIN_ACTION.IN_BATTERY]: {
    title: "Insert battery",
    batteryIdKey: "insertedBatteryIdDT",
    batterySocKey: "insertedBatterySocDT",
    swapActionStartKey: "batteryInsertionStartFromDigitalTwin",
    swapActionCompleteKey: "batteryInsertionCompleteFromDigitalTwin",
    batteryDoorCloseConfirmKey: "insertedBatteryDoorCloseConf",
  },
  [DIGITAL_TWIN_ACTION.OUT_BATTERY]: {
    title: "Take out battery",
    batteryIdKey: "takenBatteryIdDT",
    batterySocKey: "takenBatterySocDT",
    swapActionStartKey: "batteryOutStartFromDigitalTwin",
    swapActionCompleteKey: "batteryOutCompleteFromDigitalTwin",
    batteryDoorCloseConfirmKey: "takenBatteryDoorCloseConf",
  },
};

const PANEL_PADDING = { xs: 1.5, sm: 3, md: 4, lg: 5 };
const PANEL_BLEED = { xs: -1.5, sm: -3, md: -4, lg: -5 };

const isDoorOpen = (value) => value === 1;

const STEP_MAP = {
  IN_BATTERY: {
    doorOpened: {
      step: "DOOR_OPENED_IN",
      stepLabel: "Opening door to insert battery",
    },
    doorClosed: {
      step: "DOOR_CLOSED_IN",
      stepLabel: "Door closed — detecting battery",
    },
    batteryDetected: {
      step: "BATTERY_DETECTED_IN",
      stepLabel: "Battery detected in station",
    },
  },
  OUT_BATTERY: {
    doorOpened: {
      step: "DOOR_OPENED_OUT",
      stepLabel: "Opening door to take battery",
    },
    doorClosed: {
      step: "DOOR_CLOSED_OUT",
      stepLabel: "Door closed — battery taken",
    },
  },
};

const DOOR_COMMAND_TIMEOUT_MS = 30000;

const DoorPanel = ({
  action,
  station,
  onSessionStep,
  otherSessions,
  onOutDoorOpened,
}) => {
  const { data, updateData } = useStepperContext();
  const {
    title,
    batteryIdKey,
    batterySocKey,
    swapActionStartKey,
    swapActionCompleteKey,
    batteryDoorCloseConfirmKey,
  } = CONFIG[action];
  const { canIds, soc, voltage, doorStatus, eS, chargerStatus } = station;

  const [selectedDoor, setSelectedDoor] = useState(null);
  const [status, setStatus] = useState({
    isDoorOpen: false,
    isDoneDoorClose: false,
    isCompleted: false,
    isInitDoorOpen: false,
    initDoorNumber: null,
    isDoorOpenMarkedClose: false,
    isBatteryDetected: false,
    isTimerCompleted2: false,
  });

  const doorCommandTimeoutRef = useRef(null);

  const clearDoorCommandTimeout = () => {
    if (doorCommandTimeoutRef.current) {
      clearTimeout(doorCommandTimeoutRef.current);
      doorCommandTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearDoorCommandTimeout();
  }, []);

  const getOtherOperatorForDoor = (doorIndex) => {
    if (!otherSessions || otherSessions.length === 0) return null;
    return otherSessions.find((s) => s.doorNumber === doorIndex) || null;
  };

  const handleCheckSelectedDoorStatus = (doorNo) => {
    updateData(batteryIdKey, canIds[doorNo]);
    updateData(batterySocKey, soc[doorNo]);
    updateData("batteryTakenFromDoor", doorNo);
  };

  const handleInitDoorStatus = (doorNo) => {
    const selectedDoorStatus = doorStatus[doorNo];
    setStatus((prev) => ({
      ...prev,
      isDoorOpen: isDoorOpen(selectedDoorStatus),
    }));
  };

  const handleSelectedDoor = (doorNo) => {
    setSelectedDoor(doorNo);
    handleInitDoorStatus(doorNo);
    if (action === DIGITAL_TWIN_ACTION.OUT_BATTERY)
      handleCheckSelectedDoorStatus(doorNo);
  };

  const handleActionComplete = () => {
    updateData(swapActionCompleteKey, true);
  };

  const handleCompleteSwap = () => {
    setStatus((prev) => ({ ...prev, isCompleted: true }));

    if (
      action === DIGITAL_TWIN_ACTION.OUT_BATTERY ||
      data.swapReason === SKIP_BATTERY_DETECTION_REASON
    ) {
      setStatus((prev) => ({ ...prev, isBatteryDetected: true }));
    }

    if (action === DIGITAL_TWIN_ACTION.IN_BATTERY) {
      updateData("batteryInsertedIntoDoor", selectedDoor);
      updateData("batteryDoorClosedAt", Date.now());
    }

    handleActionComplete();
  };

  const handleDoorStatusUpdate = (doorStatuses, selected, prevStatus) => {
    if (!doorStatuses || selected === null) {
      return prevStatus;
    }

    const currentDoorStatus = doorStatuses[selected];

    const updatedStatus = {
      ...prevStatus,
      isDoorOpen: isDoorOpen(currentDoorStatus),
    };

    if (
      !prevStatus.isDoorOpen &&
      isDoorOpen(currentDoorStatus) &&
      prevStatus.isInitDoorOpen
    ) {
      clearDoorCommandTimeout();

      if (action === DIGITAL_TWIN_ACTION.OUT_BATTERY) {
        onOutDoorOpened?.();
      }
    }

    if (
      prevStatus.isInitDoorOpen &&
      prevStatus.isDoorOpen &&
      !isDoorOpen(currentDoorStatus)
    ) {
      updatedStatus.isDoneDoorClose = true;
      updateData(batteryDoorCloseConfirmKey, ACTION_TYPE.AUTO);

      const stepKey =
        action === DIGITAL_TWIN_ACTION.IN_BATTERY
          ? "IN_BATTERY"
          : "OUT_BATTERY";
      onSessionStep?.({
        ...STEP_MAP[stepKey].doorClosed,
        doorNumber: selected,
      });

      handleCompleteSwap();
    }

    return updatedStatus;
  };

  useEffect(() => {
    setStatus((prevStatus) =>
      handleDoorStatusUpdate(doorStatus, selectedDoor, prevStatus),
    );
  }, [selectedDoor, doorStatus]);

  useEffect(() => {
    if (
      selectedDoor === status.initDoorNumber &&
      action === DIGITAL_TWIN_ACTION.IN_BATTERY &&
      canIds[selectedDoor]
    ) {
      updateData(
        batteryIdKey,
        canIds[selectedDoor] || data.insertedBatteryIdDT,
      );
      updateData(batterySocKey, soc[selectedDoor] || data.insertedBatterySocDT);
      updateData("batteryInsertedDetectionConf", ACTION_TYPE.AUTO);
      updateData("batteryInsertedIntoDoor", selectedDoor);
      setStatus((prev) => ({ ...prev, isBatteryDetected: true }));

      onSessionStep?.({
        ...STEP_MAP.IN_BATTERY.batteryDetected,
        doorNumber: selectedDoor,
        batteryId: canIds[selectedDoor],
      });

      if (status.isDoneDoorClose) handleActionComplete();
    }
  }, [selectedDoor, action, canIds, soc, status.initDoorNumber]);

  const handleMarkedDoorClose = () => {
    updateData(batteryDoorCloseConfirmKey, ACTION_TYPE.MANUAL);
    setStatus((prev) => ({
      ...prev,
      isDoorOpenMarkedClose: true,
      isDoneDoorClose: true,
      isDoorOpen: false,
    }));

    const stepKey =
      action === DIGITAL_TWIN_ACTION.IN_BATTERY ? "IN_BATTERY" : "OUT_BATTERY";
    onSessionStep?.({
      ...STEP_MAP[stepKey].doorClosed,
      doorNumber: selectedDoor,
    });

    handleCompleteSwap();
  };

  const handleTimerCompleted2 = () => {
    setStatus((prev) => ({ ...prev, isTimerCompleted2: true }));
  };

  const handleOpenDoorCommand = async () => {
    const deviceId = data.deviceId;
    if (deviceId) {
      const payload = { doorId: selectedDoor, deviceId };
      setStatus((prev) => ({
        ...prev,
        isInitDoorOpen: true,
        initDoorNumber: selectedDoor,
      }));
      updateData(swapActionStartKey, true);

      const stepKey =
        action === DIGITAL_TWIN_ACTION.IN_BATTERY
          ? "IN_BATTERY"
          : "OUT_BATTERY";
      onSessionStep?.({
        ...STEP_MAP[stepKey].doorOpened,
        doorNumber: selectedDoor,
        isFirstDoor: action === DIGITAL_TWIN_ACTION.IN_BATTERY,
      });

      clearDoorCommandTimeout();

      doorCommandTimeoutRef.current = setTimeout(() => {
        setStatus((prev) => {
          if (!prev.isDoorOpen && prev.isInitDoorOpen) {
            return {
              ...prev,
              isInitDoorOpen: false,
              initDoorNumber: null,
            };
          }
          return prev;
        });
        updateData(swapActionStartKey, false);
      }, DOOR_COMMAND_TIMEOUT_MS);

      try {
        await axios.post(endpoints.openDoorCommand.post, payload);
      } catch (error) {
        console.error("Error posting data:", error);
        clearDoorCommandTimeout();
        setStatus((prev) => ({
          ...prev,
          isInitDoorOpen: false,
          initDoorNumber: null,
        }));
      }
    }
  };

  const handleDoorEnabledDisabled = (index) => {
    if (action === DIGITAL_TWIN_ACTION.OUT_BATTERY) {
      const insertedId = data.insertedBatteryIdDT || data.insertedBatteryId;
      return (
        !canIds[index] ||
        canIds[index] === "" ||
        insertedId === canIds[index] ||
        (status.isInitDoorOpen && status.initDoorNumber !== index)
      );
    }
    return status.isInitDoorOpen && status.initDoorNumber !== index;
  };

  return (
    <Box
      sx={{
        border: "1px solid #E3E8EE",
        borderRadius: 4,
        bgcolor: "#FFFFFF",
        boxShadow:
          "0 1px 2px rgba(16,24,40,.04), 0 8px 24px rgba(16,24,40,.04)",
        p: PANEL_PADDING,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: { xs: 1.5, sm: 2.5 },
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: 18, sm: 20 },
            fontWeight: 600,
            color: "#101828",
          }}
        >
          {title}
        </Typography>
        {status.isCompleted ? (
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              bgcolor: "#EAF7EF",
              border: "1px solid #B7E4C7",
              color: "#1B7F43",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Completed
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13, color: "#667085" }}>
            {selectedDoor === null
              ? "Select a door"
              : `Door selected: ${selectedDoor + 1}`}
          </Typography>
        )}
      </Box>

      <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
        {new Array(8).fill(0).map((_, index) => (
          <Grid key={index} item xs={12} md={6}>
            <DoorCard
              data={{ canIds, doorStatus, soc, voltage }}
              index={index}
              selectedDoor={selectedDoor}
              onClick={handleSelectedDoor}
              disabled={handleDoorEnabledDisabled(index)}
              batteryState={eS?.[index]}
              chargerOn={chargerStatus?.[index] === 1}
              otherOperator={getOtherOperatorForDoor(index)}
            />
          </Grid>
        ))}
      </Grid>

      {selectedDoor !== null && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 5,
            mt: 2,
            mx: PANEL_BLEED,
            mb: PANEL_BLEED,
            px: PANEL_PADDING,
            py: 2.5,
            bgcolor: "rgba(255,255,255,.94)",
            backdropFilter: "blur(10px)",
            borderTop: "1px solid #E3E8EE",
            borderRadius: "0 0 16px 16px",
            boxShadow: "0 -8px 20px rgba(16,24,40,.06)",
          }}
        >
          <DoorActionButtons
            status={status}
            action={action}
            selectedDoor={selectedDoor}
            handleOpenDoorCommand={handleOpenDoorCommand}
            handleTimerCompleted2={handleTimerCompleted2}
            handleMarkedDoorClose={handleMarkedDoorClose}
          />
        </Box>
      )}
    </Box>
  );
};

export default DoorPanel;
